<template>
  <!-- Mobile: Direct BetSlip rendering -->
  <div
    v-if="isMobile && isOpen"
    class="mobile-betslip-panel"
  >
    <div
      class="mobile-betslip-drawer-handle"
      aria-hidden="true"
    >
      <span class="mobile-betslip-drawer-bar" />
    </div>
    <div class="mobile-betslip-header">
      <h3 class="mobile-betslip-title">
        {{ $t('betslip.title') }}
      </h3>
      <button
        class="mobile-close-button"
        @click="close"
      >
        ×
      </button>
    </div>
    <div class="mobile-betslip-content">
      <BetSlip @close="close" />
    </div>
  </div>

  <!-- Desktop: Modal overlay -->
  <transition name="modal-slide-down">
    <div
      v-if="!isMobile && isOpen"
      class="desktop-modal-overlay"
      @click.self="close"
    >
      <div class="desktop-modal-box">
        <button
          class="desktop-close-button"
          type="button"
          :aria-label="$t('betslip.closeBetslip')"
          @click="close"
        >
          ×
        </button>
        <div class="desktop-modal-content">
          <BetSlip @close="close" />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import BetSlip from '@/components/BetSlip.vue'
import { iframeCommunication } from '@/utils/iframeCommunication';
import { ref, onMounted, onUnmounted, watch, defineProps, defineEmits } from 'vue'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits(['close'])

const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.matchMedia('(max-width: 768px)').matches
}

const isInIframe = iframeCommunication.isRunningInIframe()

// Prevent body scrolling when mobile panel is open (standalone only; iframe uses setMobileBetslipIframeOpen)
const preventBodyScroll = () => {
  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.width = '100%'
}

const restoreBodyScroll = () => {
  document.body.style.overflow = ''
  document.body.style.position = ''
  document.body.style.width = ''
}

const syncMobileBetslipScrollLock = (isOpen: boolean) => {
  if (isInIframe) {
    iframeCommunication.setMobileBetslipIframeOpen(isOpen && isMobile.value)
    return
  }

  if (!isMobile.value) {
    restoreBodyScroll()
    return
  }

  if (isOpen) {
    preventBodyScroll()
  } else {
    restoreBodyScroll()
  }
}

// Watch for mobile panel open/close
watch(() => props.isOpen, (isOpen) => {
  syncMobileBetslipScrollLock(isOpen)
})

watch(isMobile, () => {
  syncMobileBetslipScrollLock(props.isOpen)
})

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
  syncMobileBetslipScrollLock(false)
})

const close = () => emit('close')
</script>

<style scoped>
/* Mobile slide-up panel styles */
.mobile-betslip-panel {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  background-color: var(--colour-surface-cont-lowest);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  transform: translateY(0);
  transition: transform 0.3s ease-in-out;
  color: var(--colour-base-text-colour);
  padding-bottom: env(safe-area-inset-bottom);
}

.mobile-betslip-drawer-handle {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  padding: calc(6px + env(safe-area-inset-top, 0px)) 0 4px;
  background-color: var(--surface-detail-header, var(--colour-surface-cont-lowest));
}

.mobile-betslip-drawer-bar {
  display: block;
  width: 32px;
  height: 3px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--colour-base-text-colour) 16%, transparent);
}

.mobile-betslip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px 14px;
  border-top: 1px solid var(--colour-surface-cont);
  border-bottom: 2px solid var(--colour-surface-cont);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
  background-color: var(--surface-detail-header);
  position: sticky;
  top: 0;
  z-index: 1;
}

.mobile-betslip-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  flex: 1;
  color: var(--text-detail-header);
}

.mobile-close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: var(--text-detail-header);
  cursor: pointer;
  padding: 8px;
  line-height: 1;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.mobile-close-button:hover {
  background-color: var(--tab-button-inactive);
}

.mobile-betslip-content {
  flex: 1;
  overflow-y: auto;
  height: 0;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  padding-bottom: calc(20px + env(safe-area-inset-bottom));
  background-color: var(--colour-surface-cont-lowest);
}

.mobile-betslip-content :deep(.betslip) {
  background: transparent;
  border: none;
  box-shadow: none;
}

.mobile-betslip-content :deep(.betslip-header) {
  display: none;
}

/* Desktop modal styles */
.desktop-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--shadow-color);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 8px;
  overflow-y: auto;
}

.desktop-modal-box {
  background-color: var(--colour-surface-cont-lowest);
  width: 100%;
  max-width: 1280px;
  padding: 0;
  position: relative;
  color: var(--colour-base-text-colour);
  max-height: 90vh;
  overflow-y: auto;
  border-radius: var(--betslip-modal-desktop-radius, 8px);
  margin: 0 auto;
  box-sizing: border-box;
  border: var(--betslip-modal-desktop-border-width, 0) solid var(--betslip-modal-desktop-border-color, var(--colour-surface-cont));
}

.desktop-close-button {
  position: absolute;
  top: var(--betslip-modal-close-top, 16px);
  right: var(--betslip-modal-close-right, 16px);
  z-index: 3;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--betslip-modal-close-color, var(--colour-base-text-colour));
  cursor: pointer;
  padding: 4px;
}

.desktop-close-button:hover {
  opacity: 0.75;
}

.desktop-modal-content {
  padding: 0;
  background-color: transparent;
}

.desktop-modal-content :deep(.betslip) {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: var(--betslip-modal-desktop-content-padding, 16px);
}

.desktop-modal-content :deep(.betslip-header) {
  padding-right: var(--betslip-modal-header-close-space, 40px);
}

/* Responsive behavior */
@media (max-width: 768px) {
  .desktop-modal-overlay {
    display: none;
    padding: 0;
  }
  
  .desktop-modal-box {
    padding: 0;
    max-width: 100%;
    margin: 0;
  }
  
  .mobile-betslip-panel {
    height: 100vh;
  }
  
  .mobile-betslip-header {
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  .mobile-close-button {
    touch-action: manipulation;
  }
  
  :global(body.iframe-mode) .mobile-betslip-panel {
    height: 100vh !important;
    min-height: 100vh !important;
    max-height: 100vh !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    padding-bottom: 20px;
    z-index: 10001 !important;
  }
}

@media (min-width: 769px) {
  .mobile-betslip-panel {
    display: none;
  }
  
  .desktop-modal-overlay {
    padding: 8px;
  }
  
  .desktop-modal-box {
    padding: 0;
    max-width: 1280px;
  }
}

/* Slide down animation shared with BaseModal */
.modal-slide-down-enter-active,
.modal-slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-slide-down-enter-from,
.modal-slide-down-leave-to {
  opacity: 0;
}

.modal-slide-down-enter-from .desktop-modal-box,
.modal-slide-down-leave-to .desktop-modal-box {
  transform: translateY(-50px);
}
</style>
  
